import { SolutionDetails, CapabilityDetail, UseCaseDetail } from '@/types/solution';

// Simple media URL validation
const validateMediaUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  try {
    new URL(url); // Basic URL validation
    return url.includes('youtube.com') || url.includes('youtu.be') ||
           url.match(/\.(mp4|webm|mov|jpg|jpeg|png|gif|webp|svg)$/i) !== null;
  } catch {
    return false;
  }
};

// Ultra-aggressive JSON cleaning function to handle comments and malformed JSON
export function cleanJsonText(jsonText: string): string {
  console.log('Cleaning JSON text, length:', jsonText.length);
  
  try {
    // Step 1: Handle inline comments - split by lines and process each line
    const lines = jsonText.split('\n');
    const cleanedLines = lines.map((line, index) => {
      let cleanLine = line;
      
      // Handle // comments - but be careful about URLs
      const commentMatch = cleanLine.match(/^([^"]*(?:"[^"]*"[^"]*)*?)\s*\/\/.*$/);
      if (commentMatch) {
        cleanLine = commentMatch[1].trim();
        console.log(`Line ${index}: Removed comment from:`, line);
        console.log(`Line ${index}: Result:`, cleanLine);
      }
      
      return cleanLine;
    });
    
    // Step 2: Rejoin and handle block comments
    let result = cleanedLines.join('\n');
    result = result.replace(/\/\*[\s\S]*?\*\//g, ''); // Remove /* */ comments
    
    // Step 3: Handle trailing commas
    result = result.replace(/,(\s*[}\]])/g, '$1');
    
    // Step 4: Replace control characters
    // eslint-disable-next-line no-control-regex
    result = result.replace(/[\x00-\x1F\x7F]/g, ' ');
    
    // Step 5: Normalize whitespace
    result = result.replace(/\s+/g, ' ').trim();
    
    console.log('JSON cleaning completed successfully');
    return result;
    
  } catch (error) {
    console.error('Error during JSON cleaning:', error);
    return jsonText; // Return original if cleaning fails
  }
}

// Extract JSON from AI response content
export function extractJsonFromContent(content: string): string {
  let jsonText = content;
  
  // Strategy 1: Extract from code blocks
  const codeBlockMatch = content.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/);
  if (codeBlockMatch) {
    jsonText = codeBlockMatch[1];
    console.log('Extracted JSON from code block');
  } else {
    // Strategy 2: Extract largest JSON object
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      jsonText = jsonMatch[0];
      console.log('Extracted JSON object from content');
    }
  }
  
  return jsonText;
}

// Create basic fallback content when parsing fails
export function createBasicFallback(solutionName: string): SolutionDetails {
  return {
    metadata: {
      name: solutionName,
      shortDescription: `${solutionName} is an AI solution with advanced capabilities.`,
      tags: ['ai-tool']
    },
    overview: `${solutionName} is an innovative AI solution that provides advanced capabilities for various applications. This solution offers comprehensive functionality designed to meet diverse user needs.`,
    capabilities: [
      {
        title: 'Core Functionality',
        description: `Primary capabilities of ${solutionName} including advanced processing and analysis features.`
      }
    ],
    useCases: [
      {
        title: 'General Applications',
        description: `${solutionName} can be applied across various domains and use cases.`
      }
    ],
    limitations: [
      'Specific limitations may vary based on implementation',
      'Performance depends on usage context and requirements'
    ],
    longDescription: `${solutionName} represents an advanced AI solution designed to provide comprehensive functionality across various applications. The solution incorporates modern techniques and approaches to deliver effective results for users.`,
    officialWebsite: {
      title: solutionName,
      url: 'https://example.com'
    },
    externalLinks: [],
    versions: [
      {
        version: '1.0',
        status: 'released',
        since: new Date().toISOString().split('T')[0],
        tags: ['current']
      }
    ],
    createdBy: 'Unknown'
  };
}

// Process AI response and extract solution details
export function processAIResponse(content: string, solutionName: string): SolutionDetails {
  try {
    // Extract JSON from content
    let jsonText = extractJsonFromContent(content);
    
    // Clean the JSON text
    jsonText = cleanJsonText(jsonText);
    console.log('JSON cleaning completed, attempting parse...');
    
    // Parse the JSON
    const parsedContent = JSON.parse(jsonText) as SolutionDetails;
    console.log('Successfully parsed JSON content');
    
    return parsedContent;
    
  } catch (parseError) {
    console.error('JSON parsing error:', parseError);
    console.error('Raw content preview:', content.substring(0, 500));
    
    // Create basic fallback
    const fallback = createBasicFallback(solutionName);
    console.log('Using basic fallback content');
    
    return fallback;
  }
}

export function validateCapability(capability: any): string[] {
  const errors: string[] = [];
  
  if (!capability.title || typeof capability.title !== 'string') {
    errors.push("Capability title is required and must be a string");
  }
  
  if (!capability.description || typeof capability.description !== 'string') {
    errors.push("Capability description is required and must be a string");
  }
  
  if (capability.mediaLink && !validateMediaUrl(capability.mediaLink)) {
    errors.push("Invalid media URL format - must be a valid media URL (YouTube or direct image/video)");
  }
  
  return errors;
}

export function validateUseCase(useCase: any): string[] {
  const errors: string[] = [];
  
  if (!useCase.title || typeof useCase.title !== 'string') {
    errors.push("Use case title is required and must be a string");
  }
  
  if (!useCase.description || typeof useCase.description !== 'string') {
    errors.push("Use case description is required and must be a string");
  }
  
  if (useCase.mediaLink && !validateMediaUrl(useCase.mediaLink)) {
    errors.push("Invalid media URL format - must be a valid media URL (YouTube or direct image/video)");
  }
  
  return errors;
}

export function validateSolutionDetails(details: SolutionDetails): string[] {
  const errors: string[] = [];
  
  // Validate capabilities if they exist
  if (details.capabilities && Array.isArray(details.capabilities)) {
    details.capabilities.forEach((capability, index) => {
      if (typeof capability === 'object' && capability !== null) {
        const capabilityErrors = validateCapability(capability);
        capabilityErrors.forEach(error => {
          errors.push(`Capability ${index + 1}: ${error}`);
        });
      }
    });
  }
  
  // Validate use cases if they exist
  if (details.useCases && Array.isArray(details.useCases)) {
    details.useCases.forEach((useCase, index) => {
      if (typeof useCase === 'object' && useCase !== null) {
        const useCaseErrors = validateUseCase(useCase);
        useCaseErrors.forEach(error => {
          errors.push(`Use Case ${index + 1}: ${error}`);
        });
      }
    });
  }
  
  return errors;
} 